import React, { useState, useEffect, useMemo } from "react";
import { Badge } from '../components/StatusBadge';
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService, JobRows } from "../services/api";
import { Job, JobArtifacts, JobSource, JobNotification } from "../types";
import InlineFeedback from "../components/InlineFeedback";
import ConfirmDialog from "../components/ConfirmDialog";
import { useFeedback } from "../hooks/useFeedback";
import { useCopyToClipboard } from "../hooks/useCopyToClipboard";
import CopyIconButton from "../components/CopyIconButton";
import { SortableTh, compareRowValues, SortOrder } from "../components/table/TableKit";
import { notificationService } from "../services/notifications";
import { formatDateTime, formatDuration, formatTime, getRelativeTime } from '../utils/dateTime';
import { formatEstimateCents } from '../components/converter/cliCost';
import { useJobLogPolling } from "../hooks/useJobLogPolling";
import { urlBool, urlEnum, useUrlState } from '../hooks/useUrlState';
import { fromHere, useBackTo } from '../hooks/useBackTo';
import {
  ArrowDownTrayIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  SparklesIcon,
  EnvelopeIcon,
  XMarkIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  DocumentIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";

const compareWithNullsLast = (a: unknown, b: unknown, order: 'asc' | 'desc') => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  const result = compareRowValues(a, b);
  return order === 'desc' ? -result : result;
};

const fetchLogPage = (jobId: string, afterId?: number) => apiService.getJobLogs(jobId, afterId);
const fetchAllLogs = (jobId: string) => apiService.getAllJobLogs(jobId);

const URL_SPEC = {
  tab: urlEnum(['sources', 'notifications', 'logs'] as const, 'sources'),
  files: urlBool(),
  level: urlEnum(['', 'INFO', 'WARNING', 'ERROR'] as const, ''),
  'sources.sort': urlEnum(['', 'name', 'source_type', 'parse_time'] as const, ''),
  'sources.order': urlEnum(['asc', 'desc'] as const, 'desc'),
  'notifications.sort': urlEnum(['', 'level', 'type', 'operation', 'message'] as const, ''),
  'notifications.order': urlEnum(['asc', 'desc'] as const, 'desc'),
};

const JobDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backHref = useBackTo('/');
  const queryClient = useQueryClient();
  // The cost estimate is for saved instances only. The API omits it for temp instances.
  const showCost = apiService.getInstanceInfo()?.instanceMode === 'persistent';
  const [, forceUpdate] = useState({});
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [contactForm, setContactForm] = useState({ email: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [state, set] = useUrlState(URL_SPEC);
  const [sourcesDisplayCount, setSourcesDisplayCount] = useState(100);
  const [notificationsDisplayCount, setNotificationsDisplayCount] = useState(100);
  const activeTab = state.tab;
  const showFilesOnly = state.files;
  const selectedNotificationLevel = state.level || null;
  const sourcesSort = { sort: state['sources.sort'], order: state['sources.order'] };
  const notificationsSort = { sort: state['notifications.sort'], order: state['notifications.order'] };
  const onSourcesSort = (sort: string, order: SortOrder) => set({
    'sources.sort': sort as typeof state['sources.sort'],
    'sources.order': order,
  });
  const onNotificationsSort = (sort: string, order: SortOrder) => set({
    'notifications.sort': sort as typeof state['notifications.sort'],
    'notifications.order': order,
  });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const contactFeedback = useFeedback(0);
  const deleteFeedback = useFeedback();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [downloadError, setDownloadError] = useState<{ path: string; message: string } | null>(null);
  const logsCopy = useCopyToClipboard();

  const {
    data: job,
    isLoading,
    error: jobError,
  } = useQuery({
    queryKey: ["job", id],
    queryFn: () => apiService.getJob(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data as Job | undefined;
      return data?.status === "running" || data?.status === "pending" ? 2000 : false;
    },
  });

  const { logs, failed: logsFailed } = useJobLogPolling(id, job?.status, fetchLogPage, fetchAllLogs);

  const { data: artifacts, error: artifactsError } = useQuery<JobArtifacts>({
    queryKey: ["artifacts", id],
    queryFn: () => apiService.getJobArtifacts(id!),
    enabled: !!id,
  });

  const { data: sourcesPage, error: sourcesError } = useQuery<JobRows<JobSource>>({
    queryKey: ["sources", id],
    queryFn: () => apiService.getJobSources(id!),
    enabled: !!id && job?.status === "completed",
  });
  const sources = sourcesPage?.items;

  const { data: notificationsPage, error: notificationsError } = useQuery<JobRows<JobNotification>>({
    queryKey: ["notifications", id],
    queryFn: () => apiService.getJobNotifications(id!),
    enabled: !!id && job?.status === "completed",
  });
  const notifications = notificationsPage?.items;

  // The server caps one page, so a big job can hide rows.
  const hiddenSources = (sourcesPage?.total ?? 0) - (sources?.length ?? 0);
  const hiddenNotifications = (notificationsPage?.total ?? 0) - (notifications?.length ?? 0);

  const deleteMutation = useMutation({
    mutationFn: () => apiService.deleteJob(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      navigate(backHref);
    },
    onError: (error: any) => {
      setShowDeleteConfirm(false);
      deleteFeedback.showError(error.response?.data?.message || "Failed to delete job.");
    },
  });

  // Fetch artifacts, sources and notifications when job is completed
  useEffect(() => {
    if (!job?.status || !id) return;
    
    if (job.status === "completed") {
      console.log(`Job completed, fetching artifacts, sources and notifications...`);
      queryClient.invalidateQueries({ queryKey: ["artifacts", id] });
      queryClient.invalidateQueries({ queryKey: ["sources", id] });
      queryClient.invalidateQueries({ queryKey: ["notifications", id] });
    }
  }, [job?.status, id, queryClient]);

  // Track previous status to detect transitions
  const [prevStatus, setPrevStatus] = useState<string | null>(null);

  // Show notifications only when job status changes to final state
  useEffect(() => {
    if (!job || !id) return;

    // Initialize prevStatus with current status on first render to prevent notifications on mount
    if (prevStatus === null) {
      setPrevStatus(job.status);
      return;
    }

    // Skip if status hasn't changed
    if (prevStatus === job.status) return;

    // Show notification for completed or failed status (not cancelled since it's user-initiated)
    if (job.status === 'completed') {
      notificationService.showJobCompleted(job.project_name || 'Job', id);
    } else if (job.status === 'failed') {
      notificationService.showJobFailed(job.project_name || 'Job', id, job.error_message);
    }

    // Update previous status
    setPrevStatus(job.status);
  }, [job, id, prevStatus]);

  // Update elapsed time every second for running jobs
  useEffect(() => {
    if (job?.status === "running") {
      const interval = setInterval(() => {
        forceUpdate({});
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [job?.status]);

  const getTransformedFilename = (filename: string) => {
    // For Alchemist dashboard files, transform to new format
    if (filename.startsWith('alchemist_analysis_dashboard_')) {
      const match = filename.match(/_(\d{8})_(\d{6})\.(xlsx|xls|html)$/);
      if (match) {
        const [, dateStr, , ext] = match;
        // Format date as YYYY-MM-DD
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        const projectName = job?.project_name || 'Report';
        const formattedDate = `${year}-${month}-${day}`;
        // Replace spaces and special characters with underscores for filename
        const sanitizedProjectName = projectName.replace(/[^a-zA-Z0-9]/g, '_');
        return `Analysis_Report_${sanitizedProjectName}_${formattedDate}.${ext}`;
      }
    }
    return filename;
  };

  const handleDownload = async (artifactPath: string, fileName: string) => {
    setDownloadError(null);
    try {
      const blob = await apiService.downloadArtifact(id!, artifactPath);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Use transformed filename for download
      a.download = getTransformedFilename(fileName);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download error:', error);
      const errorMessage = error.response?.data?.message ||
                          error.message ||
                          `Failed to download ${fileName}.`;
      setDownloadError({ path: artifactPath, message: errorMessage });
    }
  };

  const getFileExtension = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    // Map extensions to friendly names
    const extensionMap: { [key: string]: string } = {
      'xlsx': 'Excel',
      'xls': 'Excel',
      'zip': 'ZIP archive',
      'parquet': 'Parquet',
      'csv': 'CSV',
      'json': 'JSON',
      'html': 'HTML',
      'pdf': 'PDF',
      'txt': 'Text'
    };
    
    // Check if it's a parquet files zip
    if (filename.includes('parquet') && ext === 'zip') {
      return 'Parquet files archive';
    }
    
    return extensionMap[ext] || ext.toUpperCase();
  };


  const copyLogsToClipboard = () => {
    const logText = logs.map((log) => `[${formatTime(log.timestamp)}] ${log.level}: ${log.message}`).join("\n");
    logsCopy.copy(logText);
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    contactFeedback.clear();

    try {
      await apiService.submitContactRequest({
        request_type: "analyzer_plus",
        ...contactForm,
      });
      setContactSubmitted(true);
      setContactForm({ email: "", note: "" });
    } catch (error) {
      contactFeedback.showError("Failed to send request. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const closeContactPopup = () => {
    setShowContactPopup(false);
    setContactForm({ email: "", note: "" });
    setContactSubmitted(false);
    contactFeedback.clear();
  };


  const getElapsedTime = () => {
    if (!job) return "-";
    return formatDuration(job.started_at, job.completed_at);
  };

  // Filter sources based on files only toggle
  const filteredSources = useMemo(() => {
    if (!sources) return [];
    if (!showFilesOnly) return sources;
    // Show only items with source_type exactly "File" (case insensitive)
    return sources.filter(source => 
      source.source_type && source.source_type.toLowerCase() === 'file'
    );
  }, [sources, showFilesOnly]);

  const sortedSources = useMemo(() => {
    if (!sourcesSort.sort) return filteredSources;

    const getValue = (source: JobSource) => {
      switch (sourcesSort.sort) {
        case 'name': return source.name;
        case 'source_type': return source.source_type;
        case 'parse_time': return source.parse_time ?? null;
        default: return null;
      }
    };

    return [...filteredSources].sort((a, b) =>
      compareWithNullsLast(getValue(a), getValue(b), sourcesSort.order)
    );
  }, [filteredSources, sourcesSort.sort, sourcesSort.order]);

  // Count files for the filter card
  const fileCount = useMemo(() => {
    if (!sources) return 0;
    return sources.filter(source => 
      source.source_type && source.source_type.toLowerCase() === 'file'
    ).length;
  }, [sources]);

  // Reset display count when filter changes
  useEffect(() => {
    setSourcesDisplayCount(100);
  }, [showFilesOnly]);

  // Filter notifications based on selected level
  const filteredNotifications = useMemo(() => {
    if (!notifications) return [];
    if (!selectedNotificationLevel) return notifications;
    return notifications.filter(notification => 
      notification.level === selectedNotificationLevel
    );
  }, [notifications, selectedNotificationLevel]);

  const sortedNotifications = useMemo(() => {
    if (!notificationsSort.sort) return filteredNotifications;

    const severityRank: Record<JobNotification['level'], number> = { ERROR: 2, WARNING: 1, INFO: 0 };
    const getValue = (notification: JobNotification) => {
      switch (notificationsSort.sort) {
        case 'level': return severityRank[notification.level];
        case 'type': return notification.type;
        case 'operation': return notification.operation;
        case 'message': return notification.message;
        default: return null;
      }
    };

    return [...filteredNotifications].sort((a, b) =>
      compareWithNullsLast(getValue(a), getValue(b), notificationsSort.order)
    );
  }, [filteredNotifications, notificationsSort.sort, notificationsSort.order]);

  // Count notifications by level
  const notificationCounts = useMemo(() => {
    if (!notifications) return { INFO: 0, WARNING: 0, ERROR: 0 };
    return notifications.reduce((acc, notification) => {
      if (notification.level === 'INFO') acc.INFO++;
      else if (notification.level === 'WARNING') acc.WARNING++;
      else if (notification.level === 'ERROR') acc.ERROR++;
      return acc;
    }, { INFO: 0, WARNING: 0, ERROR: 0 });
  }, [notifications]);

  // Reset display count when notification filter changes
  useEffect(() => {
    setNotificationsDisplayCount(100);
  }, [selectedNotificationLevel]);

  if (isLoading) {
    return <div className="text-center py-8 text-secondary">Loading...</div>;
  }

  // Only when nothing loaded: a failed poll must not wipe a job already on screen.
  if (jobError && !job) {
    const notFound = (jobError as any)?.response?.status === 404;
    return (
      <div className={`text-center py-8 ${notFound ? 'text-secondary' : 'text-red-400'}`}>
        {notFound ? 'Job not found' : 'Failed to load the job.'}
      </div>
    );
  }

  if (!job) {
    return <div className="text-center py-8 text-secondary">Job not found</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div className="flex items-end gap-3">
          <h2 className="text-2xl font-bold">{job.project_name || `Job ${job.id}`}</h2>
          <Badge value={job.status} size="sm" />
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            onClick={() => {
              deleteFeedback.clear();
              setShowDeleteConfirm(true);
            }}
            disabled={deleteMutation.isPending}
            className="btn-danger"
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </button>
          <InlineFeedback feedback={deleteFeedback.feedback} />
        </div>
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        title={job.status === "running" || job.status === "pending" ? "Cancel Job" : "Delete Job"}
        message={
          job.status === "running" || job.status === "pending"
            ? "This stops the job. It stays in the list as cancelled. Delete it again to remove it."
            : "This deletes the job and its results. You cannot undo it."
        }
        confirmLabel={job.status === "running" || job.status === "pending" ? "Stop Job" : "Delete Job"}
        danger
        pending={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 space-y-6">
          {/* Job Information */}
          <div className="bg-widget-bg rounded-lg border border-secondary p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Job Information</h3>
              {/* Usage is for saved instances. Jobs from before the usage log have no entries, so no link. */}
              {showCost && job.input_tokens != null && (
                <Link
                  to={`/usage/web/jobs/${job.id}`}
                  state={fromHere(location)}
                  className="text-primary hover:underline"
                >
                  Open usage
                </Link>
              )}
            </div>
            <dl className="grid grid-cols-2 gap-4">
              {/* First Column */}
              <div>
                <dt className="text-secondary text-sm">Job ID</dt>
                <dd className="text-heading font-mono flex items-center gap-2">
                  {job.id}
                  <CopyIconButton text={job.id} label="Job ID" />
                </dd>
              </div>
              {/* Second Column */}
              <div>
                <dt className="text-secondary text-sm">Created</dt>
                <dd className="text-heading">
                  {formatDateTime(job.created_at)}
                  {job.created_at && (
                    <span className="text-secondary ml-1">({getRelativeTime(job.created_at)})</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-secondary text-sm">Operation</dt>
                <dd className="text-heading">{job.operation}</dd>
              </div>
              {job.started_at && (
                <div>
                  <dt className="text-secondary text-sm">Started</dt>
                  <dd className="text-heading">
                    {formatDateTime(job.started_at)}
                    <span className="text-secondary ml-1">({getRelativeTime(job.started_at)})</span>
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-secondary text-sm">Time Elapsed</dt>
                <dd className="text-heading">{getElapsedTime()}</dd>
              </div>
              {job.completed_at && (
                <div>
                  <dt className="text-secondary text-sm">Completed</dt>
                  <dd className="text-heading">
                    {formatDateTime(job.completed_at)}
                    <span className="text-secondary ml-1">({getRelativeTime(job.completed_at)})</span>
                  </dd>
                </div>
              )}
              {job.supported_tokens != null && (
                <div>
                  <dt className="text-secondary text-sm">Supported Tokens</dt>
                  <dd className="text-heading">{job.supported_tokens.toLocaleString()}</dd>
                </div>
              )}
              {job.supported_tokens != null && showCost && (
                <div>
                  <dt className="text-secondary text-sm">Est. Conversion Cost</dt>
                  <dd className="text-heading">{formatEstimateCents(job.estimated_conversion_cost_cents)}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Progress */}
          {job.status === "running" && (
            <div className="bg-widget-bg rounded-lg border border-secondary p-6">
              <h3 className="text-lg font-semibold mb-4">Progress</h3>
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-secondary">Progress</span>
                  <span className="text-heading">{job.progress}%</span>
                </div>
                <div className="w-full bg-body-bg rounded-full h-3">
                  <div className="bg-primary h-3 rounded-full transition-all duration-300" style={{ width: `${job.progress}%` }} />
                </div>
              </div>
              {job.current_step && (
                <p className="text-sm text-secondary">
                  Current step: <span className="text-heading">{job.current_step}</span>
                </p>
              )}
            </div>
          )}

          {/* Error Message */}
          {job.error_message && (
            <div className="bg-red-500/25 border border-red-500 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 text-red-500">Error</h3>
              <p className="text-red-400 font-mono text-sm">{job.error_message}</p>
            </div>
          )}

          {/* Tabs */}
          <div>
            {/* Tabs Header */}
            <div className="flex border-b border-secondary">
              <button 
                className={`px-6 py-3 font-semibold transition-colors ${
                  activeTab === 'sources'
                    ? 'text-heading border-b-2 border-primary' 
                    : 'text-secondary hover:text-heading'
                }`}
                onClick={() => set({ tab: 'sources' })}
              >
                Sources
              </button>
              <button 
                className={`px-6 py-3 font-semibold transition-colors ${
                  activeTab === 'notifications'
                    ? 'text-heading border-b-2 border-primary' 
                    : 'text-secondary hover:text-heading'
                }`}
                onClick={() => set({ tab: 'notifications' })}
              >
                Notifications
              </button>
              <button 
                className={`px-6 py-3 font-semibold transition-colors ${
                  activeTab === 'logs'
                    ? 'text-heading border-b-2 border-primary' 
                    : 'text-secondary hover:text-heading'
                }`}
                onClick={() => set({ tab: 'logs' })}
              >
                Logs
              </button>
            </div>
              
              {/* Space between tabs header and content */}
              <div className="mt-6">
                {/* Sources Content */}
                {activeTab === 'sources' && (
                  <div>
                    {/* File Filter Card */}
                    {sources && sources.length > 0 && fileCount > 0 && (
                      <div className="mb-4">
                        <div className="flex gap-3">
                          <button
                            type="button"
                            className={`bg-widget-bg rounded-lg px-4 py-3 border cursor-pointer text-left transition-all hover:scale-105 ${
                              showFilesOnly ? 'border-primary ring-2 ring-primary/50' : 'border-secondary'
                            }`}
                            onClick={() => set({ files: !showFilesOnly })}
                          >
                            <div className="flex items-center gap-2">
                              <DocumentIcon className="w-5 h-5 text-secondary" />
                              <span className="text-sm font-medium text-secondary">Files</span>
                              <span className="text-sm font-semibold text-heading">{fileCount}</span>
                            </div>
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="bg-widget-bg rounded-lg border border-secondary">
                      <div className="p-6">
                        {sourcesError && !sourcesPage ? (
                          <p className="text-red-400 text-center py-8">Failed to load sources.</p>
                        ) : sortedSources.length > 0 ? (
                          <div>
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-secondary">
                                <SortableTh label="Name" sortKey="name" sort={sourcesSort.sort} order={sourcesSort.order} onSort={onSourcesSort} firstOrder="asc" />
                                <SortableTh label="Type" sortKey="source_type" sort={sourcesSort.sort} order={sourcesSort.order} onSort={onSourcesSort} firstOrder="asc" />
                                <SortableTh label="Parse Time (s)" sortKey="parse_time" sort={sourcesSort.sort} order={sourcesSort.order} onSort={onSourcesSort} />
                              </tr>
                            </thead>
                            <tbody>
                              {sortedSources.slice(0, sourcesDisplayCount).map((source, index) => (
                                <tr key={source.source_id} className={index < Math.min(sortedSources.length, sourcesDisplayCount) - 1 ? 'border-b border-secondary/50' : ''}>
                                  <td className="px-4 py-3 text-sm text-body-color break-words">{source.name || '-'}</td>
                                  <td className="px-4 py-3 text-sm text-body-color break-words">{source.source_type}</td>
                                  <td className="px-4 py-3 text-sm text-body-color">
                                    {source.parse_time !== null && source.parse_time !== undefined 
                                      ? source.parse_time.toFixed(2) 
                                      : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {sortedSources.length > sourcesDisplayCount && (
                            <div className="mt-4 flex items-center justify-center gap-4">
                              <button
                                onClick={() => setSourcesDisplayCount(sourcesDisplayCount + 100)}
                                className="btn-secondary px-4 py-2"
                              >
                                Show More
                              </button>
                              <span className="text-sm text-secondary">
                                Showing {Math.min(sourcesDisplayCount, sortedSources.length)} of {sortedSources.length} items
                              </span>
                            </div>
                          )}
                          {sortedSources.length > 100 && sortedSources.length <= sourcesDisplayCount && (
                            <div className="mt-4 text-center">
                              <span className="text-sm text-secondary">
                                Showing all {sortedSources.length} items
                              </span>
                            </div>
                          )}
                          {hiddenSources > 0 && (
                            <div className="mt-2 text-center">
                              <span className="text-sm text-warning">
                                This job has {hiddenSources} more sources. Download the result to see them all.
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-secondary text-center py-8">
                          {showFilesOnly ? 'No files found.' : 'No sources available.'}
                        </p>
                      )}
                    </div>
                  </div>
                  </div>
                )}

                {/* Notifications Content */}
                {activeTab === 'notifications' && (
                  <div>
                    {/* Level Filter Cards */}
                    {notifications && notifications.length > 0 && (
                      <div className="mb-4">
                        <div className="flex gap-3">
                          {notificationCounts.INFO > 0 && (
                            <button
                              type="button"
                              className={`bg-widget-bg rounded-lg px-4 py-3 border cursor-pointer text-left transition-all hover:scale-105 ${
                                selectedNotificationLevel === 'INFO' ? 'border-primary ring-2 ring-primary/50' : 'border-secondary'
                              }`}
                              onClick={() => set({ level: selectedNotificationLevel === 'INFO' ? '' : 'INFO' })}
                            >
                              <div className="flex items-center gap-2">
                                <InformationCircleIcon className="w-5 h-5 text-blue-500" />
                                <span className="text-sm font-medium text-secondary">Info</span>
                                <span className="text-sm font-semibold text-heading">{notificationCounts.INFO}</span>
                              </div>
                            </button>
                          )}
                          {notificationCounts.WARNING > 0 && (
                            <button
                              type="button"
                              className={`bg-widget-bg rounded-lg px-4 py-3 border cursor-pointer text-left transition-all hover:scale-105 ${
                                selectedNotificationLevel === 'WARNING' ? 'border-primary ring-2 ring-primary/50' : 'border-secondary'
                              }`}
                              onClick={() => set({ level: selectedNotificationLevel === 'WARNING' ? '' : 'WARNING' })}
                            >
                              <div className="flex items-center gap-2">
                                <ExclamationTriangleIcon className="w-5 h-5 text-warning" />
                                <span className="text-sm font-medium text-secondary">Warning</span>
                                <span className="text-sm font-semibold text-heading">{notificationCounts.WARNING}</span>
                              </div>
                            </button>
                          )}
                          {notificationCounts.ERROR > 0 && (
                            <button
                              type="button"
                              className={`bg-widget-bg rounded-lg px-4 py-3 border cursor-pointer text-left transition-all hover:scale-105 ${
                                selectedNotificationLevel === 'ERROR' ? 'border-primary ring-2 ring-primary/50' : 'border-secondary'
                              }`}
                              onClick={() => set({ level: selectedNotificationLevel === 'ERROR' ? '' : 'ERROR' })}
                            >
                              <div className="flex items-center gap-2">
                                <ExclamationCircleIcon className="w-5 h-5 text-red-500" />
                                <span className="text-sm font-medium text-secondary">Error</span>
                                <span className="text-sm font-semibold text-heading">{notificationCounts.ERROR}</span>
                              </div>
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="bg-widget-bg rounded-lg border border-secondary">
                      <div className="p-6">
                        {notificationsError && !notificationsPage ? (
                          <p className="text-red-400 text-center py-8">Failed to load notifications.</p>
                        ) : sortedNotifications.length > 0 ? (
                          <div>
                            <table className="w-full">
                              <thead>
                                <tr className="border-b border-secondary">
                                  <SortableTh label="Level" sortKey="level" sort={notificationsSort.sort} order={notificationsSort.order} onSort={onNotificationsSort} />
                                  <SortableTh label="Type" sortKey="type" sort={notificationsSort.sort} order={notificationsSort.order} onSort={onNotificationsSort} firstOrder="asc" />
                                  <SortableTh label="Operation" sortKey="operation" sort={notificationsSort.sort} order={notificationsSort.order} onSort={onNotificationsSort} firstOrder="asc" />
                                  <SortableTh label="Message" sortKey="message" sort={notificationsSort.sort} order={notificationsSort.order} onSort={onNotificationsSort} firstOrder="asc" />
                                </tr>
                              </thead>
                              <tbody>
                                {sortedNotifications.slice(0, notificationsDisplayCount).map((notification, index) => (
                                  <tr key={`${notification.source_id}-${index}`} className={index < Math.min(sortedNotifications.length, notificationsDisplayCount) - 1 ? 'border-b border-secondary/50' : ''}>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center">
                                      {notification.level === 'INFO' && (
                                        <InformationCircleIcon className="w-5 h-5 text-blue-500" title="INFO" />
                                      )}
                                      {notification.level === 'WARNING' && (
                                        <ExclamationTriangleIcon className="w-5 h-5 text-warning" title="WARNING" />
                                      )}
                                      {notification.level === 'ERROR' && (
                                        <ExclamationCircleIcon className="w-5 h-5 text-red-500" title="ERROR" />
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-body-color break-words">{notification.type}</td>
                                  <td className="px-4 py-3 text-sm text-body-color break-words">{notification.operation}</td>
                                  <td className="px-4 py-3 text-sm text-body-color break-words">{notification.message || '-'}</td>
                                </tr>
                              ))}
                              </tbody>
                            </table>
                            {sortedNotifications.length > notificationsDisplayCount && (
                              <div className="mt-4 flex items-center justify-center gap-4">
                                <button
                                  onClick={() => setNotificationsDisplayCount(notificationsDisplayCount + 100)}
                                  className="btn-secondary px-4 py-2"
                                >
                                  Show More
                                </button>
                                <span className="text-sm text-secondary">
                                  Showing {Math.min(notificationsDisplayCount, sortedNotifications.length)} of {sortedNotifications.length} items
                                </span>
                              </div>
                            )}
                            {sortedNotifications.length > 100 && sortedNotifications.length <= notificationsDisplayCount && (
                              <div className="mt-4 text-center">
                                <span className="text-sm text-secondary">
                                  Showing all {sortedNotifications.length} items
                                </span>
                              </div>
                            )}
                            {hiddenNotifications > 0 && (
                              <div className="mt-2 text-center">
                                <span className="text-sm text-warning">
                                  This job has {hiddenNotifications} more notifications. Download the result to see them all.
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-secondary text-center py-8">
                            {selectedNotificationLevel 
                              ? `No ${selectedNotificationLevel.toLowerCase()} notifications found.` 
                              : 'No notifications available.'}
                          </p>
                      )}
                    </div>
                  </div>
                  </div>
                )}

                {/* Logs Content */}
                {activeTab === 'logs' && (
                  <div className="bg-widget-bg rounded-lg border border-secondary relative">
                    {logs.length > 0 ? (
                      <>
                        {/* Copy button overlay in top right */}
                        <button
                          onClick={copyLogsToClipboard}
                          className="absolute top-2 right-2 z-10 p-2 rounded-md text-secondary hover:text-primary hover:bg-primary/10 transition-colors"
                          title={logsCopy.status === 'copied' ? 'Copied!' : logsCopy.status === 'error' ? 'Failed to copy logs' : 'Copy logs to clipboard'}
                        >
                          {logsCopy.status === 'copied' ? (
                            <CheckIcon className="w-4 h-4 text-primary" />
                          ) : logsCopy.status === 'error' ? (
                            <XMarkIcon className="w-4 h-4 text-red-400" />
                          ) : (
                            <DocumentDuplicateIcon className="w-4 h-4" />
                          )}
                        </button>
                        
                        <div className="p-6">
                          {logsFailed && (
                            <p className="text-red-400 text-sm mb-4 pr-10">
                              Failed to load all logs. Showing what loaded.
                            </p>
                          )}
                          <div className="space-y-1 font-mono text-sm">
                            {logs.map((log, index) => (
                              <div key={log.id || index} className="flex gap-2">
                                <span className="text-secondary whitespace-nowrap">{formatTime(log.timestamp)}</span>
                                <span
                                  className={log.level === "ERROR" ? "text-red-500" : log.level === "WARNING" ? "text-warning" : "text-body-color"}
                                >
                                  {log.message}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="p-6">
                        <p className={`${logsFailed ? 'text-red-400' : 'text-secondary'} text-center py-8`}>
                          {logsFailed ? 'Failed to load logs.' : 'No logs available'}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
        </div>

        {/* Artifacts */}
        <div className="space-y-6">
          {/* Basic Analyzer Artifacts */}
          <div className="bg-widget-bg rounded-lg border border-secondary h-fit">
            <div className="px-6 py-4 border-b border-secondary">
              <h3 className="text-lg font-semibold">Artifacts</h3>
            </div>
            <div className="p-6">
              {job?.status !== "completed" ? (
                <p className="text-secondary text-center py-4">No artifacts available</p>
              ) : artifactsError && !artifacts ? (
                <p className="text-red-400 text-center py-4">Failed to load artifacts.</p>
              ) : (
                <div className="space-y-3">
                  {/* Static Dashboard - Always show first for completed jobs */}
                  <div 
                    className="p-3 bg-body-bg rounded-lg border border-secondary hover:border-primary btn-clickable"
                    onClick={() => navigate(`/jobs/${job.id}/dashboard`, { state: fromHere(location) })}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-heading text-sm">
                          Analysis Dashboard
                        </p>
                        <p className="text-xs text-secondary">View analysis results</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <ChartBarIcon className="w-4 h-4 text-primary" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Basic artifacts */}
                  {artifacts?.basic_artifacts && artifacts.basic_artifacts.map((artifact) => {
                    const filename = artifact.file_path.split("/").pop() || '';
                    const isAnalysisReport = filename.startsWith('alchemist_analysis_dashboard_');
                    
                    return (
                      <div
                        key={artifact.id}
                        className="p-3 bg-body-bg rounded-lg border border-secondary hover:border-primary btn-clickable"
                        onClick={() => handleDownload(artifact.file_path, artifact.file_path.split("/").pop()!)}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-heading text-sm truncate" title={artifact.file_path.split("/").pop()}>
                              {(() => {
                                // For Alchemist dashboard files, show a shorter version
                                if (isAnalysisReport) {
                                  const match = filename.match(/_(\d{8})_(\d{6})\.(xlsx|xls|html)$/);
                                  if (match) {
                                    const [, dateStr, , ext] = match;
                                    // Format date as YYYY-MM-DD
                                    const year = dateStr.substring(0, 4);
                                    const month = dateStr.substring(4, 6);
                                    const day = dateStr.substring(6, 8);
                                    const projectName = job?.project_name || 'Report';
                                    const formattedDate = `${year}-${month}-${day}`;
                                    return `Analysis Report: ${projectName} ${formattedDate}.${ext}`;
                                  }
                                }
                                return filename;
                              })()}
                            </p>
                            <p className="text-xs text-secondary">
                              {artifact.file_size ? `${(artifact.file_size / 1024 / 1024).toFixed(2)} MB` : "Unknown size"}{(() => {
                                const ext = getFileExtension(artifact.file_path.split("/").pop() || '');
                                return ext ? ` • ${ext}` : '';
                              })()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <ArrowDownTrayIcon className="w-4 h-4 text-primary" />
                          </div>
                        </div>
                        {downloadError?.path === artifact.file_path && (
                          <InlineFeedback
                            feedback={{ kind: "error", message: downloadError.message }}
                            className="mt-2"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Analyzer Plus Card - Always show for completed jobs */}
          {job?.status === "completed" && (
            <div className="bg-widget-bg rounded-lg border border-secondary h-fit">
              <div className="px-6 py-4 border-b border-secondary">
                <div className="flex items-center gap-2">
                  <SparklesIcon className="h-6 w-6 text-primary" />
                  <h3 className="text-lg font-semibold">Analyzer Plus Artifacts</h3>
                </div>
              </div>
              <div className="p-6">
                {artifacts?.analyzer_plus_artifacts && artifacts.analyzer_plus_artifacts.length > 0 ? (
                  <div className="space-y-3">
                    {artifacts.analyzer_plus_artifacts.map((artifact) => {
                      const filename = artifact.file_path.split("/").pop() || '';
                      const isAnalysisReport = filename.startsWith('alchemist_analysis_dashboard_');
                      
                      return (
                        <div
                          key={artifact.id}
                          className="p-3 bg-body-bg rounded-lg border border-secondary hover:border-primary btn-clickable"
                          onClick={() => handleDownload(artifact.file_path, artifact.file_path.split("/").pop()!)}
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-heading text-sm truncate" title={artifact.file_path.split("/").pop()}>
                                {(() => {
                                  // For Alchemist dashboard files, show a shorter version
                                  if (isAnalysisReport) {
                                    const match = filename.match(/_(\d{8})_(\d{6})\.(xlsx|xls|html)$/);
                                    if (match) {
                                      const [, dateStr, , ext] = match;
                                      // Format date as YYYY-MM-DD
                                      const year = dateStr.substring(0, 4);
                                      const month = dateStr.substring(4, 6);
                                      const day = dateStr.substring(6, 8);
                                      const projectName = job?.project_name || 'Report';
                                      const formattedDate = `${year}-${month}-${day}`;
                                      return `Analysis Report: ${projectName} ${formattedDate}.${ext}`;
                                    }
                                  }
                                  return filename;
                                })()}
                              </p>
                              <p className="text-xs text-secondary">
                                {artifact.file_size ? `${(artifact.file_size / 1024 / 1024).toFixed(2)} MB` : "Unknown size"}{(() => {
                                  const ext = getFileExtension(artifact.file_path.split("/").pop() || '');
                                  return ext ? ` • ${ext}` : '';
                                })()}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <ArrowDownTrayIcon className="w-4 h-4 text-primary" />
                            </div>
                          </div>
                          {downloadError?.path === artifact.file_path && (
                            <InlineFeedback
                              feedback={{ kind: "error", message: downloadError.message }}
                              className="mt-2"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-body-color text-sm">
                      Get detailed data sets from your source code.
                    </p>
                    <button
                      onClick={() => setShowContactPopup(true)}
                      className="btn-secondary w-full flex items-center justify-center py-2"
                    >
                      <EnvelopeIcon className="h-5 w-5 mr-2" />
                      Contact Us
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contact Us Popup */}
      {showContactPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-widget-bg rounded-lg p-8 max-w-md w-full mx-4 border border-secondary shadow-xl">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-heading">Get Analyzer Plus</h2>
              <button
                onClick={closeContactPopup}
                className="text-body-color hover:text-heading transition-colors p-1 hover:bg-body-bg/50 rounded"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <p className="text-body-color mb-6">
              Analyzer Plus gives detailed data sets from your source code. Use them to plan your migration.
            </p>
            {contactSubmitted ? (
              <div className="space-y-4">
                <InlineFeedback
                  feedback={{ kind: "success", message: "We got your request. We will contact you soon." }}
                />
                <button onClick={closeContactPopup} className="w-full btn-primary-full">
                  Done
                </button>
              </div>
            ) : (
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm text-secondary mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full px-3 py-3 bg-body-bg border border-secondary rounded-md text-body-color placeholder-secondary/50 focus:outline-none focus:border-primary transition-colors"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label htmlFor="note" className="block text-sm text-secondary mb-2">
                  Message
                </label>
                <textarea
                  id="note"
                  rows={4}
                  required
                  value={contactForm.note}
                  onChange={(e) => setContactForm({ ...contactForm, note: e.target.value })}
                  className="w-full px-3 py-3 bg-body-bg border border-secondary rounded-md text-body-color placeholder-secondary/50 focus:outline-none focus:border-primary transition-colors resize-none"
                  placeholder="Tell us about your migration needs..."
                />
              </div>
              <InlineFeedback feedback={contactFeedback.feedback} />
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 btn-primary-full"
                >
                  {submitting ? "Submitting..." : "Submit Request"}
                </button>
                <button
                  type="button"
                  onClick={closeContactPopup}
                  className="btn-secondary py-3"
                >
                  Cancel
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetail;
